import { ROOT } from '../env'
import { Logger, PlayerSlot } from '../utils'
import { fillAbsClass } from '../styles'
import type { HostPayload, PlayerBridge } from '../types'
import Hls from 'hls.js'

type LivePlayerKind = 'hls' | 'mp4' | 'mp3'

export class LiveVideoPlayer implements PlayerBridge {
  private video: HTMLVideoElement | null = null
  private hls: Hls | null = null
  private currentSource = ''
  private data: HostPayload | null = null
  private hlsIsLive = true  // assume live until manifest proves otherwise
  private pendingSeek: number | null = null
  private seekVerifyTimer: ReturnType<typeof setTimeout> | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private retryAttempt = 0
  private readonly retryDelaysMs = [700, 1200, 1800, 2600, 3600, 5000, 7000, 9000, 12000, 16000, 22000, 30000]
  private mediaRecoveryAttempts = 0
  private stallErrorCount = 0
  private stallResetTimer: ReturnType<typeof setTimeout> | null = null
  private desiredHlsQuality = 'auto'
  private autoLevelCap = -1
  private stablePlaybackTimer: ReturnType<typeof setTimeout> | null = null
  private isAudioOnlySource = false
  private slot = new PlayerSlot('live-video', ROOT)
  private logger = Logger.create('LiveVideoPlayer')
  private readonly kind: LivePlayerKind
  private readonly postMessage: (msg: string) => void

  constructor(kind: LivePlayerKind, postMessage: (msg: string) => void) {
    this.kind = kind
    this.postMessage = postMessage
  }

  load(data: HostPayload): void {
    this.data = data
    this.isAudioOnlySource = this.kind === 'mp3' || (this.kind !== 'hls' && this.isMp3Source(data.id))
    if (this.kind === 'hls' && data.quality) {
      this.desiredHlsQuality = data.quality
    }
    const video = this.ensureVideoElement()
    video.loop = this.isAudioOnlySource

    const sourceChanged = this.currentSource !== data.id
    if (sourceChanged) {
      this.clearRetryTimer()
      this.retryAttempt = 0
      if (this.kind === 'hls') this.hlsIsLive = true  // reset until manifest re-parsed
    }
    this.currentSource = data.id

    const resumePlayback = (): void => {
      // Live HLS streams use a sliding window — don't seek to an absolute position.
      // VOD HLS and mp4 support seeking.
      const isLiveHls = this.kind === 'hls' && this.hlsIsLive
      if (!isLiveHls && !this.isAudioOnlySource && data.seek > 0) {
        this.applySeek(data.seek)
      }

      video.volume = Math.max(0, Math.min(1, data.volume / 100))
      const playPromise = video.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => {
          this.logger.warn('live playback start blocked', err)
        })
      }

      this.postMessage('playbackReady')
    }

    const bindReadinessAndResume = (): void => {
      const onReady = (): void => {
        video.removeEventListener('canplay', onReady)
        resumePlayback()
      }

      video.addEventListener('canplay', onReady)
    }

    if (this.kind === 'hls') {
      const nativeHls = video.canPlayType('application/vnd.apple.mpegurl')

      if (Hls.isSupported()) {
        if (!this.hls) {
          this.hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            backBufferLength: 30,
            maxBufferLength: 30,
            maxMaxBufferLength: 60,
            liveSyncDurationCount: 4,
            liveMaxLatencyDurationCount: 8,
          })

          this.hls.on(Hls.Events.ERROR, (_event, payload) => {
            this.logger.warn('hls.js error', payload)
            if (payload?.fatal) {
              this.handleFatalHlsError(payload)
              return
            }

            this.handleNonFatalHlsError(payload)
          })

          this.hls.on(Hls.Events.MANIFEST_PARSED, (_event, manifestData) => {
            this.retryAttempt = 0
            this.mediaRecoveryAttempts = 0
            this.resetStallState()
            this.resetAutoQualityCap()
            this.applyDesiredHlsQuality()
            // Detect live vs VOD: live playlists have no total duration
            this.hlsIsLive = manifestData.levels?.[0]?.details?.live ?? true
            resumePlayback()
          })

          this.hls.on(Hls.Events.LEVEL_SWITCHED, (_event, payload) => {
            const levelIndex = typeof payload.level === 'number' ? payload.level : -1
            if (levelIndex === -1) {
              this.postMessage('hlsQuality=auto')
              return
            }

            const levels = this.hls?.levels ?? []
            const level = levels[levelIndex]
            if (!level) return

            const height = typeof level.height === 'number' && level.height > 0
              ? String(level.height)
              : `level-${levelIndex}`
            this.postMessage(`hlsQuality=${height}p`)
          })

          this.hls.attachMedia(video)
        }

        if (sourceChanged) {
          this.hls.loadSource(data.id)
        } else {
          resumePlayback()
        }

        return
      }

      if (nativeHls) {
        if (sourceChanged) {
          video.src = data.id
          video.load()
          bindReadinessAndResume()
        } else {
          resumePlayback()
        }
        return
      }

      this.logger.warn('No HLS support detected (native and hls.js unavailable).')
      this.postMessage('onPlayerError=2')
      return
    }

    if (sourceChanged) {
      video.src = data.id
      video.load()
      bindReadinessAndResume()
      return
    }

    if (!sourceChanged) {
      resumePlayback()
      return
    }
  }

  handleCommand(msg: string): boolean {
    const video = this.video

    if (msg === 'durationCheck') {
      const duration = video?.duration ?? 0
      this.postMessage(`durationCheck=${Math.round(Number.isFinite(duration) ? duration : 0)}`)
      return true
    }

    if (msg === 'unMute') {
      if (video) video.muted = false
      return true
    }

    if (msg.startsWith('stopVideo')) {
      video?.pause()
      return true
    }

    if (msg.startsWith('setVolume=')) {
      const volume = parseInt(msg.split('=').pop() || '', 10)
      if (video && !Number.isNaN(volume)) {
        video.volume = Math.max(0, Math.min(1, volume / 100))
      }
      return true
    }

    if (msg.startsWith('setPlaybackQuality=')) {
      const quality = msg.split('=').pop() || 'auto'
      if (this.kind === 'hls') {
        this.desiredHlsQuality = quality
        this.applyDesiredHlsQuality()
      }
      return true
    }

    if (msg.startsWith('seekTo=')) {
      const seconds = parseInt(msg.split('=').pop() || '', 10)
      if (video && !Number.isNaN(seconds)) {
        const isLiveStream = this.kind === 'hls' && this.hlsIsLive
        if (isLiveStream || this.isAudioOnlySource) {
          // Ignore seek commands for live streams — timeline is sliding-window based.
          return true
        }

        this.applySeek(seconds)
      }
      return true
    }

    if (msg === 'pauseVideo') {
      const isLiveStream = this.kind === 'hls' && this.hlsIsLive
      if (video && !this.isAudioOnlySource && !isLiveStream) {
        video.pause()
        this.logger.debug('pauseVideo')
      }
      return true
    }

    if (msg === 'playVideo') {
      const isLiveStream = this.kind === 'hls' && this.hlsIsLive
      if (video && !this.isAudioOnlySource && !isLiveStream) {
        const p = video.play()
        if (p && typeof p.catch === 'function') p.catch(() => {})
        this.logger.debug('playVideo')
      }
      return true
    }

    return false
  }

  destroy(): void {
    this.currentSource = ''
    this.clearRetryTimer()
    this.clearStallResetTimer()
    this.clearStablePlaybackTimer()
    this.clearSeekVerifyTimer()
    this.pendingSeek = null
    this.retryAttempt = 0
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }

    if (this.video) {
      this.video.pause()
      this.video.src = ''
      this.video.removeAttribute('src')
      this.video.load()
      this.video = null
    }
    this.slot.destroy()
  }

  private ensureVideoElement(): HTMLVideoElement {
    if (this.video) return this.video

    this.slot.show()
    const video = this.slot.node({
      tag: 'video',
      key: 'live-video-element',
      className: fillAbsClass,
      attrs: {
        autoplay: 'autoplay',
        playsinline: 'playsinline',
        preload: 'auto',
      },
    }) as HTMLVideoElement

    video.controls = false
    video.muted = false

    video.addEventListener('timeupdate', () => {
      this.postMessage(`currentTime=${video.currentTime}`)
      // Verify pending seek landed — Firefox/Edge sometimes silently ignore seeks
      // when the video wasn't buffered enough; retry once on the next timeupdate.
      if (this.seekVerifyTimer !== null && this.pendingSeek !== null) {
        const drift = Math.abs(video.currentTime - this.pendingSeek)
        if (drift <= 2) {
          // Seek landed — clear both
          clearTimeout(this.seekVerifyTimer)
          this.seekVerifyTimer = null
          this.pendingSeek = null
        }
      }
    })

    video.addEventListener('canplay', () => {
      // Apply any seek that was requested before the video was ready
      if (this.pendingSeek !== null) {
        this.applySeek(this.pendingSeek)
      }
    })

    video.addEventListener('ended', () => {
      if (this.isAudioOnlySource) return
      this.postMessage('playbackComplete')
    })

    video.addEventListener('error', () => {
      const err = video.error
      this.logger.warn('native video error', { code: err?.code, message: err?.message, src: video.src })
      this.handleNativeVideoError()
    })

    video.addEventListener('loadedmetadata', () => {
      const duration = Number.isFinite(video.duration) ? Math.round(video.duration) : 0
      this.postMessage(`durationCheck=${duration}`)
    })

    this.video = video
    return video
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }

  private applySeek(seconds: number): void {
    const video = this.video
    if (!video) return

    this.pendingSeek = seconds

    // If not yet seekable (readyState < HAVE_METADATA), defer to canplay
    if (video.readyState < 1) return

    try {
      video.currentTime = seconds
    } catch {
      return
    }

    // Schedule verification — if timeupdate doesn't confirm landing within 2s, retry once
    if (this.seekVerifyTimer !== null) clearTimeout(this.seekVerifyTimer)
    this.seekVerifyTimer = setTimeout(() => {
      this.seekVerifyTimer = null
      if (this.pendingSeek === null || !this.video) return
      const drift = Math.abs(this.video.currentTime - this.pendingSeek)
      if (drift > 2) {
        this.logger.warn('Seek did not land, retrying', { target: this.pendingSeek, actual: this.video.currentTime })
        try { this.video.currentTime = this.pendingSeek } catch {}
      }
      this.pendingSeek = null
    }, 2000)
  }

  private clearSeekVerifyTimer(): void {
    if (this.seekVerifyTimer !== null) {
      clearTimeout(this.seekVerifyTimer)
      this.seekVerifyTimer = null
    }
  }

  private clearStablePlaybackTimer(): void {
    if (this.stablePlaybackTimer !== null) {
      clearTimeout(this.stablePlaybackTimer)
      this.stablePlaybackTimer = null
    }
  }

  private clearStallResetTimer(): void {
    if (this.stallResetTimer !== null) {
      clearTimeout(this.stallResetTimer)
      this.stallResetTimer = null
    }
  }

  private scheduleRetry(reason: string): void {
    if (!this.currentSource) {
      this.postMessage('onPlayerError=2')
      return
    }

    if (this.retryTimer !== null) {
      return
    }

    if (this.retryAttempt >= this.retryDelaysMs.length) {
      this.logger.warn(`Retry limit reached for live stream (${reason})`, {
        source: this.currentSource,
        attempts: this.retryAttempt,
      })
      this.postMessage('onPlayerError=2')
      return
    }

    const delay = this.retryDelaysMs[this.retryAttempt] as number
    this.retryAttempt += 1
    this.degradeAutoQualityCap()

    this.logger.warn(`Retrying live stream in ${delay}ms`, {
      reason,
      attempt: this.retryAttempt,
      source: this.currentSource,
    })

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.reloadCurrentSource()
    }, delay)
  }

  private reloadCurrentSource(): void {
    const source = this.currentSource
    const video = this.video

    if (!source || !video) {
      this.postMessage('onPlayerError=2')
      return
    }

    if (this.kind === 'hls' && this.hls) {
      this.hls.stopLoad()
      this.hls.loadSource(source)
      this.hls.startLoad(-1)
      this.nudgeLiveEdge('reload')
      return
    }

    video.src = source
    video.load()
  }

  private handleFatalHlsError(payload: unknown): void {
    const error = payload as {
      type?: string
      details?: string
      fatal?: boolean
      response?: { code?: number }
    }

    if (!error?.fatal) return

    const code = error.response?.code
    const details = error.details || 'unknown'
    const type = error.type || 'unknown'

    if (type === 'networkError' || details.includes('manifestLoad') || code === 404 || code === 0) {
      this.scheduleRetry(`hls:${type}:${details}:${code ?? 'n/a'}`)
      return
    }

    if (type === 'mediaError' && this.hls && this.mediaRecoveryAttempts < 2) {
      this.mediaRecoveryAttempts += 1
      this.logger.warn('Attempting hls.js media recovery', {
        attempt: this.mediaRecoveryAttempts,
        details,
      })

      try {
        this.hls.recoverMediaError()
        this.scheduleRetry(`hls-media-recover:${this.mediaRecoveryAttempts}:${details}`)
        return
      } catch (err) {
        this.logger.warn('hls.js media recovery failed', err)
      }
    }

    // For any other fatal class, retry instead of failing immediately.
    this.scheduleRetry(`hls-fatal:${type}:${details}:${code ?? 'n/a'}`)
    if (this.retryTimer !== null) return

    this.postMessage('onPlayerError=2')
  }

  private handleNonFatalHlsError(payload: unknown): void {
    const error = payload as {
      type?: string
      details?: string
    }

    const type = error.type || ''
    const details = error.details || ''
    if (type !== 'mediaError' || details !== 'bufferStalledError') return

    this.stallErrorCount += 1
    this.clearStallResetTimer()
    this.stallResetTimer = setTimeout(() => {
      this.resetStallState()
    }, 5000)

    this.nudgeLiveEdge(`stall-${this.stallErrorCount}`)

    if (this.stallErrorCount >= 3) {
      this.logger.warn('Repeated HLS stalls detected, forcing source reload', {
        source: this.currentSource,
        stallErrorCount: this.stallErrorCount,
      })
      this.resetStallState()
      this.scheduleRetry('hls-nonfatal-buffer-stall')
    }
  }

  private nudgeLiveEdge(reason: string): void {
    if (this.kind !== 'hls' || !this.hlsIsLive || !this.hls || !this.video) return

    const liveSyncPosition = this.hls.liveSyncPosition
    if (typeof liveSyncPosition === 'number' && Number.isFinite(liveSyncPosition)) {
      try {
        // Seek slightly behind sync point to avoid immediate re-stall at exact live edge.
        this.video.currentTime = Math.max(0, liveSyncPosition - 0.4)
        this.logger.debug('Nudged playback to live edge', { reason, liveSyncPosition })
      } catch {
        // Ignore if stream/window is temporarily non-seekable.
      }
    }

    const playPromise = this.video.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Autoplay constraints may block this attempt; next tick/update may recover.
      })
    }
  }

  private resetStallState(): void {
    this.stallErrorCount = 0
    this.clearStallResetTimer()
  }

  private handleNativeVideoError(): void {
    this.scheduleRetry('native-video-error')
  }

  private applyDesiredHlsQuality(): void {
    if (this.kind !== 'hls' || !this.hls) return

    const quality = (this.desiredHlsQuality || 'auto').trim().toLowerCase()
    if (!quality || quality === 'auto' || quality === 'default' || quality === '-1') {
      this.hls.currentLevel = -1
      this.hls.autoLevelCapping = this.autoLevelCap
      this.postMessage('hlsQuality=auto')
      this.scheduleStableAutoCapReset()
      return
    }

    const levels = this.hls.levels || []
    if (levels.length === 0) {
      return
    }

    const targetHeight = this.parseQualityTargetHeight(quality)
    if (targetHeight === null) {
      this.hls.currentLevel = -1
      this.postMessage('hlsQuality=auto')
      return
    }

    const nextLevel = this.pickClosestLevelByHeight(levels, targetHeight)
    if (nextLevel === -1) {
      this.hls.currentLevel = -1
      this.postMessage('hlsQuality=auto')
      return
    }

    this.hls.currentLevel = nextLevel
    const selected = levels[nextLevel]
    if (selected && typeof selected.height === 'number' && selected.height > 0) {
      this.postMessage(`hlsQuality=${selected.height}p`)
    }
  }

  private isAutoQualityMode(): boolean {
    const quality = (this.desiredHlsQuality || 'auto').trim().toLowerCase()
    return !quality || quality === 'auto' || quality === 'default' || quality === '-1'
  }

  private degradeAutoQualityCap(): void {
    if (this.kind !== 'hls' || !this.hls || !this.isAutoQualityMode()) return

    const levels = this.hls.levels || []
    if (levels.length <= 1) return

    const maxLevel = levels.length - 1
    const currentCap = this.autoLevelCap >= 0 ? this.autoLevelCap : maxLevel
    const nextCap = Math.max(0, currentCap - 1)

    if (nextCap === this.autoLevelCap) return

    this.autoLevelCap = nextCap
    this.hls.autoLevelCapping = nextCap
    this.hls.nextLevel = nextCap

    const selected = levels[nextCap]
    const qualityLabel = selected?.height ? `${selected.height}p` : `level-${nextCap}`
    this.logger.warn('Auto quality cap degraded after stream errors', {
      capLevel: nextCap,
      quality: qualityLabel,
      retryAttempt: this.retryAttempt,
    })
  }

  private resetAutoQualityCap(): void {
    if (this.kind !== 'hls' || !this.hls) return
    this.autoLevelCap = -1
    this.hls.autoLevelCapping = -1
  }

  private scheduleStableAutoCapReset(): void {
    if (this.kind !== 'hls' || !this.hls || !this.isAutoQualityMode()) return
    this.clearStablePlaybackTimer()
    this.stablePlaybackTimer = setTimeout(() => {
      this.stablePlaybackTimer = null
      if (!this.hls || !this.isAutoQualityMode()) return
      this.resetAutoQualityCap()
      this.logger.debug('Auto quality cap reset after stable playback window')
    }, 12000)
  }

  private parseQualityTargetHeight(quality: string): number | null {
    if (quality.includes('1080')) return 1080
    if (quality.includes('720')) return 720
    if (quality.includes('480')) return 480
    if (quality.includes('360')) return 360

    const parsed = Number.parseInt(quality, 10)
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }

    return null
  }

  private pickClosestLevelByHeight(levels: Array<{ height?: number }>, targetHeight: number): number {
    let bestAtOrBelow = -1
    let bestBelowHeight = -1
    let bestAbove = -1
    let bestAboveHeight = Number.POSITIVE_INFINITY

    for (let i = 0; i < levels.length; i += 1) {
      const h = levels[i]?.height
      if (typeof h !== 'number' || h <= 0) continue

      if (h <= targetHeight && h > bestBelowHeight) {
        bestBelowHeight = h
        bestAtOrBelow = i
      }

      if (h > targetHeight && h < bestAboveHeight) {
        bestAboveHeight = h
        bestAbove = i
      }
    }

    if (bestAtOrBelow !== -1) return bestAtOrBelow
    return bestAbove
  }

  private isMp3Source(source: string): boolean {
    return /\.mp3(?:$|[?#])/i.test(source)
  }
}
